import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, KeyRound, ArrowRight, CheckCircle2 } from "lucide-react";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import SEO from "@/components/storefront/SEO";

export default function AccountReset() {
  const { resetPassword } = useCustomerAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [token, setToken] = useState(sp.get("token") || "");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = sp.get("token");
    if (t) setToken(t);
  }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("Password must be at least 6 characters");
    if (pwd !== confirm) return setError("Passwords do not match");
    setBusy(true);
    const res = await resetPassword({ token, new_password: pwd });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setDone(true);
    setTimeout(() => nav("/account", { replace: true }), 1200);
  };

  return (
    <div className="mx-auto grid min-h-[70vh] max-w-md px-6 py-14" data-testid="account-reset-page">
      <SEO title="Reset password" noindex />
      <div className="m-auto w-full">
        <div className="mb-2 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Account</div>
        <h1 className="font-display text-5xl leading-tight md:text-[60px]">Set a <span className="font-display-soft">new password</span></h1>
        <p className="mt-3 text-sm text-[#6B5158]">Paste the reset code we generated and choose a new password.</p>

        {done ? (
          <div className="mt-10 rounded-md border border-[#E6D4CC] bg-white p-6 text-center" data-testid="reset-success">
            <CheckCircle2 size={36} className="mx-auto text-[#9D5C6C]" strokeWidth={1.4} />
            <div className="mt-3 font-display text-2xl">Password updated</div>
            <p className="mt-2 text-sm text-[#6B5158]">Taking you to your account…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-4">
            <Field icon={KeyRound} label="Reset code" value={token} onChange={setToken} testId="reset-token" mono required />
            <Field icon={Lock} label="New password" type="password" autoComplete="new-password" value={pwd} onChange={setPwd} testId="reset-password" required />
            <Field icon={Lock} label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={setConfirm} testId="reset-confirm" required />

            {error && <div className="rounded-md bg-rose-50 p-3 text-xs text-rose-700" data-testid="reset-error">{error}</div>}

            <button
              type="submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2B2127] px-6 py-3.5 text-sm text-white transition-colors hover:bg-[#9D5C6C] disabled:opacity-60"
              data-testid="reset-submit"
            >
              {busy ? "Updating…" : (<>Update password <ArrowRight size={14} /></>)}
            </button>

            <p className="text-center text-xs text-[#6B5158]">
              Need a new code?{" "}
              <Link to="/account/forgot" className="underline decoration-[#9D5C6C] underline-offset-4 hover:text-[#2B2127]">Request again</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type = "text", value, onChange, testId, required, autoComplete, mono }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#6B5158]">{label}</span>
      <span className="relative block">
        <Icon size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#6B5158]" />
        <input
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className={`w-full rounded-full border border-[#E6D4CC] bg-white py-3 pl-11 pr-4 text-sm outline-none focus:border-[#2B2127] ${mono ? "font-mono" : ""}`}
          data-testid={testId}
        />
      </span>
    </label>
  );
}
